/**
 * The real implementation of `Repo`.
 *
 * Reads never filter by shop — RLS does that, and a client-side filter would be
 * a second, weaker copy of the rule that silently diverges. Writes DO carry
 * `shop_id`, because an insert has to name the row it is creating; the RLS
 * `WITH CHECK` clause is what stops that value being a lie.
 *
 * The factory takes the shop id once, at construction, so it never appears in
 * a screen's call. Every method maps database rows to the app's shapes — the
 * column names stop here.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { operationPatch, isMissingLabelColumn } from './operationLabel.js';

/**
 * The client is bound to the `app` schema, which makes its generic parameters
 * differ from the default. The repository does not care which schema the client
 * defaults to — it names its own tables — so the type is widened here rather
 * than forcing every caller to match a shape that is an implementation detail.
 */
type Db = SupabaseClient<any, any, any, any, any>;
import type {
  CatalogItem, CustomerSummary, DashboardMetrics, JobDetail, JobDraft, JobSummary,
  Repo,
} from './repo.js';
import { acceptanceByWeek, bucketByWeek, lastWeeks } from './weeks.js';
import { JOB_STATUSES } from '../fixtures/shop.js';
import type { Candidate, Verdict } from '../ai/review.js';
import type { CatalogEntry, ExclusionRule } from '../estimate/build.js';
import {
  extraShotsFor, shotListFor, type ImpactPoint, type ShotState,
} from '../capture/shotlist.js';
import { currentStage, shotsTotalFor } from '../app/jobStage.js';
import type { RateProfile } from '../calc/types.js';

/** Postgres numerics arrive as strings once they exceed what JS parses exactly. */
function num(v: unknown): number {
  const n = typeof v === 'string' ? Number(v) : typeof v === 'number' ? v : 0;
  return Number.isFinite(n) ? n : 0;
}

function vehicleLabel(v: Record<string, unknown> | null): string {
  if (!v) return '';
  return [v.year, v.make, v.model, v.trim].filter(Boolean).join(' ');
}

/**
 * Get the real message out of an Edge Function failure.
 *
 * `error.context` is the raw `Response`. Reading it is the only way to see what
 * the function actually said.
 */
async function explain(error: unknown): Promise<string> {
  const ctx = (error as { context?: unknown }).context;
  const fallback = error instanceof Error ? error.message : String(error);
  if (!ctx || typeof (ctx as Response).text !== 'function') return fallback;
  try {
    const body = await (ctx as Response).text();
    if (!body) return fallback;
    try {
      const parsed = JSON.parse(body);
      return typeof parsed?.error === 'string' ? parsed.error : body.slice(0, 300);
    } catch {
      return body.slice(0, 300);
    }
  } catch {
    return fallback;
  }
}

export function createSupabaseRepo(db: Db, shopId: string): Repo {
  /** Every query goes through here so a thrown error is never a silent empty list. */
  async function rows<T>(
    label: string,
    q: PromiseLike<{ data: T[] | null; error: { message: string } | null }>,
  ): Promise<T[]> {
    const { data, error } = await q;
    if (error) throw new Error(`${label}: ${error.message}`);
    return data ?? [];
  }

  return {
    async dashboardMetrics(): Promise<DashboardMetrics> {
      const window = lastWeeks(12, Date.now());
      const since = new Date(window.starts[0]!).toISOString();

      const [jobRows, decisions, prompts, openJobRows] = await Promise.all([
        rows<any>('metrics.jobs', db
          .from('jobs')
          .select('status, estimate_versions ( grand_total, version_no )')),
        rows<any>('metrics.decisions', db
          .from('damage_proposals')
          .select('verdict, decided_at')
          .neq('verdict', 'pending')
          .gte('decided_at', since)),
        // Each `asked_once` row IS one prompt the estimator answered. There is
        // no separate prompt log, and inventing one would let the two numbers
        // disagree about the same event.
        rows<any>('metrics.prompts', db
          .from('catalog_parts')
          .select('updated_at')
          .eq('source', 'asked_once')
          .gte('updated_at', since)),
        // A second, narrower pass at exactly the jobs a stage can apply to.
        // The summary query above stays cheap for every other status; this
        // one carries the extra columns only where they mean anything.
        rows<any>('metrics.openJobs', db
          .from('jobs')
          .select(`reference, impact_point, customers ( name ),
                    photos ( slot ), damage_proposals ( verdict )`)
          .eq('status', 'open')
          .limit(200)),
      ]);

      const count = (s: string) => jobRows.filter((j) => j.status === s).length;
      const latestTotal = (j: any) => {
        const v = (j.estimate_versions ?? [])
          .slice().sort((a: any, b: any) => b.version_no - a.version_no)[0];
        return v ? num(v.grand_total) : 0;
      };

      const staged = openJobRows.map((j) => {
        const shotsTaken = new Set(
          (j.photos ?? []).map((p: any) => p.slot).filter(Boolean)).size;
        const shotsTotal = shotsTotalFor(j.impact_point ?? '');
        const proposals = j.damage_proposals ?? [];
        const proposalsDecided = proposals.filter((p: any) => p.verdict !== 'pending').length;
        const stage = currentStage({
          shotsTaken, shotsTotal,
          proposalsTotal: proposals.length, proposalsDecided,
        });
        const shotsLeft = Math.max(0, shotsTotal - shotsTaken);
        const proposalsLeft = proposals.length - proposalsDecided;
        const reason = stage === 'capture'
          ? `${shotsLeft} photograph${shotsLeft === 1 ? '' : 's'} still needed`
          : stage === 'review'
            ? `${proposalsLeft} proposal${proposalsLeft === 1 ? '' : 's'} still need a verdict`
            : 'reviewed — ready for you to finish pricing';
        return { reference: j.reference, customer: j.customers?.name ?? '', stage, reason };
      });

      return {
        openJobs: count('open'),
        awaitingApproval: count('awaiting_approval'),
        invoicedThisMonth: jobRows
          .filter((j) => j.status === 'invoiced')
          .reduce((sum, j) => sum + latestTotal(j), 0),
        weeks: window.labels,
        acceptance: acceptanceByWeek(
          decisions.map((d) => ({ verdict: d.verdict, decidedAt: d.decided_at })), window),
        catalogPrompts: bucketByWeek(prompts, (p) => p.updated_at, window)
          .map((week) => week.length),
        byStatus: JOB_STATUSES.map((status) => ({ status, count: count(status) })),
        byStage: {
          capture: staged.filter((s) => s.stage === 'capture').length,
          review: staged.filter((s) => s.stage === 'review').length,
          price: staged.filter((s) => s.stage === 'price').length,
        },
        needsYou: staged.slice(0, 3),
      };
    },

    async createJob(input): Promise<{ reference: string }> {
      // One RPC rather than four inserts. A customer, a vehicle and a job are
      // one act to the estimator, and half of one is worse than none — a
      // vehicle with no job is a row nobody will ever find again.
      const { data, error } = await db.schema('public').rpc('create_job', {
        p_shop_id: shopId,
        p_customer_id: input.customerId,
        p_customer_name: input.customerName,
        p_customer_needs_approval: input.customerNeedsApproval,
        p_customer_email: input.customerEmail,
        p_vehicle: input.vehicle,
        p_impact: input.impact,
      });
      if (error) throw new Error(`createJob: ${error.message}`);
      const row = (Array.isArray(data) ? data[0] : data) as { reference?: string } | null;
      if (!row?.reference) throw new Error('createJob: no reference returned');
      return { reference: row.reference };
    },

    async listJobs(): Promise<JobSummary[]> {
      const data = await rows<any>('listJobs', db
        .from('jobs')
        .select(`reference, status, impact_point,
                 customers ( name, needs_approval, email ),
                 vehicles ( year, make, model, trim, body_style, vin ),
                 estimate_versions ( grand_total, version_no ),
                 photos ( slot ), damage_proposals ( verdict )`)
        .order('created_at', { ascending: false })
        .limit(200));

      return data.map((j) => {
        const latest = (j.estimate_versions ?? [])
          .slice()
          .sort((a: any, b: any) => b.version_no - a.version_no)[0];
        const proposals = j.damage_proposals ?? [];
        return {
          reference: j.reference,
          customer: j.customers?.name ?? '',
          customerEmail: j.customers?.email ?? null,
          needsApproval: Boolean(j.customers?.needs_approval),
          vehicle: vehicleLabel(j.vehicles),
          vin: j.vehicles?.vin ?? null,
          impact: j.impact_point ?? '',
          status: j.status,
          total: latest ? num(latest.grand_total) : null,
          shotsTaken: new Set(
            (j.photos ?? []).map((p: any) => p.slot).filter(Boolean)).size,
          proposalsTotal: proposals.length,
          proposalsDecided: proposals.filter((p: any) => p.verdict !== 'pending').length,
        };
      });
    },

    async updateJobDetails(reference, patch) {
      if (patch.customerEmail !== undefined && patch.customerEmail.trim() === '') {
        throw new Error('updateJobDetails: an email address is required');
      }
      const { data: job, error: jobErr } = await db
        .from('jobs').select('customer_id, vehicle_id')
        .eq('reference', reference).maybeSingle();
      if (jobErr) throw new Error(`updateJobDetails: ${jobErr.message}`);
      if (!job) throw new Error(`updateJobDetails: no job ${reference}`);
      const { customer_id, vehicle_id } = job as { customer_id: string; vehicle_id: string | null };

      if (patch.vin !== undefined) {
        if (!vehicle_id) throw new Error('updateJobDetails: this job has no vehicle on file');
        const { error } = await db.from('vehicles')
          .update({ vin: patch.vin?.trim() || null }).eq('id', vehicle_id);
        if (error) throw new Error(`updateJobDetails: ${error.message}`);
      }
      if (patch.customerEmail !== undefined) {
        const { error } = await db.from('customers')
          .update({ email: patch.customerEmail.trim() }).eq('id', customer_id);
        if (error) throw new Error(`updateJobDetails: ${error.message}`);
      }
    },

    async getJob(reference): Promise<JobDetail | null> {
      const { data, error } = await db
        .from('jobs')
        .select(`id, reference, impact_point, acv, status, draft,
                 customers ( name, needs_approval, terms, email ),
                 vehicles ( year, make, model, trim ),
                 photos ( slot, quality_flag, analysed_at, storage_path ),
                 damage_proposals ( id, zone, part_label, damage_kind, operation,
                                    work_kind, confidence, observations, rationale,
                                    verdict,
                                    proposal_evidence ( photos ( slot ) ) ),
                 unassessed ( part_label, reason, photograph_wanted, resolved_at ),
                 estimate_versions ( version_no, status, issued_at, document_hash,
                                     grand_total,
                                     approval_requests ( outcome, customer_note,
                                                         responded_at, sent_to,
                                                         sent_at ) )`)
        .eq('reference', reference)
        .maybeSingle();
      if (error) throw new Error(`getJob: ${error.message}`);
      if (!data) return null;

      const j = data as any;
      const shots: ShotState[] = (j.photos ?? [])
        .filter((p: any) => p.slot)
        .map((p: any) => ({
          id: p.slot,
          status: p.quality_flag && p.quality_flag !== 'ok' ? 'retake' : 'taken',
          // Already read by a model. The button counts only unread ones, so
          // adding a photograph to a job costs one photograph, not all of them.
          analysed: Boolean(p.analysed_at),
        }));

      // Signed URLs for the photographs, so Review can show the evidence
      // rather than name it. One batch call; failures degrade to no thumbnail
      // rather than breaking the screen, because a missing picture must never
      // cost an estimator the proposal it belongs to.
      const paths = (j.photos ?? [])
        .filter((p: any) => p.slot && p.storage_path)
        .map((p: any) => p.storage_path as string);
      const photoUrls: Record<string, string> = {};
      if (paths.length > 0) {
        const { data: signed } = await db.storage
          .from('photos').createSignedUrls(paths, 60 * 60);
        const bySignedPath = new Map(
          (signed ?? []).map((row: any) => [row.path, row.signedUrl]));
        for (const p of j.photos ?? []) {
          const url = p.storage_path && bySignedPath.get(p.storage_path);
          if (p.slot && url) photoUrls[p.slot] = url as string;
        }
      }

      const candidates: Candidate[] = (j.damage_proposals ?? []).map((p: any) => ({
        id: p.id,
        zone: p.zone,
        partLabel: p.part_label,
        damageKind: p.damage_kind ?? '',
        operation: p.operation,
        workKind: p.work_kind,
        confidence: p.confidence,
        // The photographs behind the proposal, by shot slot — what the
        // "Why this?" panel shows and what the gate checks for. Every array
        // here is defaulted: a missing one used to reach `applyGate` as
        // `undefined` and take the whole job screen down with it.
        evidence: (p.proposal_evidence ?? [])
          .map((e: any) => e.photos?.slot)
          .filter((slot: unknown): slot is string => typeof slot === 'string'),
        observations: Array.isArray(p.observations) ? p.observations : [],
        rationale: p.rationale ?? '',
      }));

      // The newest ISSUED version — a superseded one is history, not the
      // document this job is currently waiting on.
      const issuedVersion = ((j.estimate_versions ?? []) as any[])
        .filter((v) => v.status === 'issued')
        .sort((a, b) => b.version_no - a.version_no)[0];
      const requests = ((issuedVersion?.approval_requests ?? []) as any[]);
      const answered = requests
        .filter((a) => a.outcome)
        .sort((a, b) => String(b.responded_at).localeCompare(String(a.responded_at)))[0];
      const lastSent = requests
        .slice()
        .sort((a, b) => String(b.sent_at).localeCompare(String(a.sent_at)))[0];

      // An answer on a version this job has replaced. It happened for real:
      // the app let an issued estimate be issued again, which superseded the
      // very document the customer had just approved.
      const staleAnswer = answered ? null : ((j.estimate_versions ?? []) as any[])
        .filter((v) => v.version_no !== issuedVersion?.version_no)
        .flatMap((v) => ((v.approval_requests ?? []) as any[])
          .filter((a) => a.outcome)
          .map((a) => ({ choice: a.outcome, at: a.responded_at, version: v.version_no })))
        .sort((a, b) => String(b.at).localeCompare(String(a.at)))[0] ?? null;

      const verdicts: Record<string, Verdict> = {};
      for (const p of j.damage_proposals ?? []) {
        if (p.verdict && p.verdict !== 'pending') {
          verdicts[`${p.zone}::${p.part_label}`] = p.verdict;
        }
      }

      // Unresolved only. Once an estimator has looked, it stops being a
      // question and becomes a record — kept, but off the working screen.
      const cannotAssess = (j.unassessed ?? [])
        .filter((u: any) => !u.resolved_at)
        .map((u: any) => ({
          partLabel: u.part_label,
          reason: u.reason,
          photographWanted: u.photograph_wanted,
        }));

      return {
        id: j.id,
        reference: j.reference,
        customer: {
          name: j.customers?.name ?? '',
          needsApproval: Boolean(j.customers?.needs_approval),
          terms: j.customers?.terms ?? '',
          email: j.customers?.email?.trim() || null,
        },
        vehicle: vehicleLabel(j.vehicles),
        bodyStyle: j.vehicles?.body_style ?? undefined,
        make: j.vehicles?.make ?? undefined,
        impact: (j.impact_point ?? 'front') as ImpactPoint,
        acv: j.acv === null || j.acv === undefined ? null : num(j.acv),
        shots,
        // The model's own photo requests, as cards the estimator can act on.
        extraShots: extraShotsFor(cannotAssess),
        photoUrls,
        draft: (j.draft ?? {}) as JobDraft,
        candidates,
        cannotAssess,
        verdicts,
        issued: issuedVersion
          ? {
              at: issuedVersion.issued_at,
              version: issuedVersion.version_no,
              hash: issuedVersion.document_hash ?? '',
              total: num(issuedVersion.grand_total),
            }
          : null,
        approval: answered
          ? { choice: answered.outcome, note: answered.customer_note,
              at: answered.responded_at }
          : null,
        approvalSentTo: lastSent?.sent_to ?? null,
        staleApproval: staleAnswer,
      };
    },

    async listCustomers(): Promise<CustomerSummary[]> {
      const data = await rows<any>('listCustomers', db
        .from('customers')
        .select(`id, name, kind, needs_approval, terms, email, phone,
                 created_at, vehicles ( id )`)
        .order('name'));

      return data.map((c) => ({
        id: c.id,
        name: c.name,
        kind: c.kind,
        needsApproval: Boolean(c.needs_approval),
        terms: c.terms ?? '',
        email: c.email ?? null,
        phone: c.phone ?? null,
        vehicles: (c.vehicles ?? []).length,
        since: String(c.created_at ?? '').slice(0, 7),
      }));
    },

    async createCustomer(input) {
      // The type says email is required; this is the backstop for a caller
      // that bypasses it. A UI validation gap must not become a customer
      // silently created without one — the same gap that let Naveed through.
      const email = input.email.trim();
      if (!email) throw new Error('createCustomer: an email address is required');
      const { data, error } = await db
        .from('customers')
        .insert({
          shop_id: shopId,
          name: input.name,
          kind: input.kind,
          needs_approval: input.needsApproval,
          email,
          phone: input.phone?.trim() || null,
          terms: input.terms?.trim() || null,
        })
        .select('id')
        .single();
      if (error) throw new Error(`createCustomer: ${error.message}`);
      return { id: (data as any).id };
    },

    async updateCustomer(id, patch) {
      const row: Record<string, unknown> = {};
      if (patch.name !== undefined) row.name = patch.name;
      if (patch.email !== undefined) row.email = patch.email?.trim() || null;
      if (patch.phone !== undefined) row.phone = patch.phone?.trim() || null;
      if (patch.terms !== undefined) row.terms = patch.terms.trim() || null;
      if (Object.keys(row).length === 0) return;
      const { error } = await db.from('customers').update(row).eq('id', id);
      if (error) throw new Error(`updateCustomer: ${error.message}`);
    },

    async setCustomerApproval(id, needsApproval) {
      const { error } = await db
        .from('customers').update({ needs_approval: needsApproval }).eq('id', id);
      if (error) throw new Error(`setCustomerApproval: ${error.message}`);
    },

    async listCatalog(): Promise<CatalogItem[]> {
      const data = await rows<any>('listCatalog', db
        .from('catalog_parts')
        .select(`part_number, description, part_type, price, source,
                 replace_hours, repair_hours, refinish_hours, body_style, make`)
        .order('description'));

      return data.map((r) => ({
        partNumber: r.part_number ?? '',
        description: r.description,
        partType: r.part_type,
        price: num(r.price),
        source: r.source,
        // NULL stays undefined, never 0: "nobody has said" and "takes no time"
        // are different claims, and only one of them should zero a line.
        ...(r.replace_hours === null ? {} : { replaceHours: num(r.replace_hours) }),
        ...(r.repair_hours === null ? {} : { repairHours: num(r.repair_hours) }),
        ...(r.refinish_hours === null ? {} : { refinishHours: num(r.refinish_hours) }),
        ...(r.body_style ? { bodyStyle: r.body_style } : {}),
        ...(r.make ? { make: r.make } : {}),
      }));
    },

    async addCatalogEntry(entry) {
      // `asked_once` is the source that matters: it is the count the dashboard
      // watches fall. Losing it would hide whether the design is working.
      const { error } = await db.from('catalog_parts').upsert({
        shop_id: shopId,
        part_number: entry.partNumber || null,
        description: entry.description,
        part_type: entry.partType,
        price: entry.price,
        replace_hours: entry.replaceHours ?? null,
        repair_hours: entry.repairHours ?? null,
        refinish_hours: entry.refinishHours ?? null,
        body_style: entry.bodyStyle ?? null,
        make: entry.make ?? null,
        source: 'asked_once',
        updated_at: new Date().toISOString(),
      }, { onConflict: 'shop_id,part_number,description,body_style,make' });
      if (error) throw new Error(`addCatalogEntry: ${error.message}`);
    },

    async removeCatalogEntry(key) {
      // NULL is not matchable with .eq, so each optional part of the key needs
      // the is-null form. Getting this wrong would delete the wrong row.
      let q = db.from('catalog_parts').delete().eq('description', key.description);
      q = key.partNumber ? q.eq('part_number', key.partNumber) : q.is('part_number', null);
      q = key.bodyStyle ? q.eq('body_style', key.bodyStyle) : q.is('body_style', null);
      q = key.make ? q.eq('make', key.make) : q.is('make', null);
      const { error } = await q;
      if (error) throw new Error(`removeCatalogEntry: ${error.message}`);
    },

    async listExclusions(): Promise<ExclusionRule[]> {
      const data = await rows<any>('listExclusions', db
        .from('include_exclude_rules')
        .select('operation, part_pattern, excludes, suggested_line'));

      return data.map((r) => ({
        operation: r.operation,
        partPattern: r.part_pattern,
        excludes: r.excludes,
        // The offered line is stored whole, because the shop authored it — a
        // reconstructed line would quietly stop matching what they wrote.
        suggests: r.suggested_line,
      })) as ExclusionRule[];
    },

    async sendForApproval(reference) {
      const { data, error } = await db.functions.invoke('approval', {
        body: { action: 'send', reference },
      });
      if (error) throw new Error(await explain(error));
      const d = data as any;
      return { sentTo: d?.sentTo ?? '', link: d?.link ?? '', emailed: Boolean(d?.emailed) };
    },

    async approvalStatus(reference) {
      // Read through the caller's own client: RLS keeps one shop out of
      // another's approvals without this having to check anything itself.
      const { data, error } = await db
        .from('approval_requests')
        .select(`outcome, customer_note, responded_at,
                 estimate_versions!inner ( jobs!inner ( reference ) )`)
        .eq('estimate_versions.jobs.reference', reference)
        .not('outcome', 'is', null)
        .order('responded_at', { ascending: false })
        .limit(1);
      if (error) throw new Error(`approvalStatus: ${error.message}`);
      const row = (data ?? [])[0] as any;
      if (!row) return null;
      return { choice: row.outcome, note: row.customer_note, at: row.responded_at };
    },

    async saveRateProfile(profile) {
      const { data: row, error: findErr } = await db
        .from('rate_profiles').select('id, shop_id').eq('is_default', true).maybeSingle();
      if (findErr) throw new Error(`saveRateProfile: ${findErr.message}`);
      if (!row) throw new Error('saveRateProfile: this shop has no default rate profile');
      const profileId = (row as any).id;

      const { error } = await db.from('rate_profiles').update({
        body_rate: profile.labourRates.body,
        refinish_rate: profile.labourRates.refinish,
        frame_rate: profile.labourRates.frame,
        mechanical_rate: profile.labourRates.mechanical,
        paint_materials_per_hour: profile.paintMaterialsPerHour,
        shop_materials_per_hour: profile.shopMaterialsPerHour,
        paint_materials_cap: profile.paintMaterialsCap ?? null,
      }).eq('id', profileId);
      if (error) throw new Error(`saveRateProfile: ${error.message}`);

      // Taxes are replaced wholesale rather than diffed: the set is tiny, and a
      // diff that mis-identified a row could leave a stale component quietly
      // adding itself to every total. sort_order carries compounding order.
      const { error: delErr } = await db
        .from('tax_components').delete().eq('profile_id', profileId);
      if (delErr) throw new Error(`saveRateProfile: ${delErr.message}`);

      if (profile.taxes.length > 0) {
        const { error: insErr } = await db.from('tax_components').insert(
          profile.taxes.map((t, i) => ({
            shop_id: (row as any).shop_id,
            profile_id: profileId,
            name: t.name,
            rate: t.rate,
            on_parts: t.onParts,
            on_labour: t.onLabour,
            on_materials: t.onMaterials,
            is_compound: Boolean(t.compound),
            sort_order: i,
          })));
        if (insErr) throw new Error(`saveRateProfile: ${insErr.message}`);
      }
    },

    async getRateProfile(): Promise<RateProfile> {
      const { data, error } = await db
        .from('rate_profiles')
        .select(`body_rate, refinish_rate, frame_rate, mechanical_rate,
                 paint_materials_per_hour, shop_materials_per_hour,
                 paint_materials_cap, total_loss_threshold_pct,
                 tax_components ( name, rate, on_parts, on_labour, on_materials,
                                  is_compound, sort_order )`)
        .eq('is_default', true)
        .maybeSingle();
      if (error) throw new Error(`getRateProfile: ${error.message}`);
      if (!data) throw new Error('getRateProfile: this shop has no default rate profile');

      const p = data as any;
      return {
        labourRates: {
          body: num(p.body_rate),
          refinish: num(p.refinish_rate),
          frame: num(p.frame_rate),
          mechanical: num(p.mechanical_rate),
        },
        paintMaterialsPerHour: num(p.paint_materials_per_hour),
        shopMaterialsPerHour: num(p.shop_materials_per_hour),
        paintMaterialsCap: p.paint_materials_cap === null ? undefined
          : num(p.paint_materials_cap),
        // sort_order is the order tax compounds in. Sorting on read means a
        // compounding component can never be applied before its base.
        taxes: (p.tax_components ?? [])
          .slice()
          .sort((a: any, b: any) => a.sort_order - b.sort_order)
          .map((t: any) => ({
            name: t.name,
            rate: num(t.rate),
            onParts: t.on_parts !== false,
            onLabour: t.on_labour !== false,
            onMaterials: t.on_materials !== false,
            compound: t.is_compound === true,
          })),
        // Which part types are taxable is a shop policy, not a per-profile
        // column; until it is one, every type but 'existing' is taxed.
        taxablePartTypes: ['new_oem', 'aftermarket', 'used', 'sublet'],
      } as RateProfile;
    },

    async recordVerdict(reference, proposalKey, verdict) {
      const [zone, partLabel] = proposalKey.split('::');
      const { data: job, error: jobErr } = await db
        .from('jobs').select('id').eq('reference', reference).maybeSingle();
      if (jobErr) throw new Error(`recordVerdict: ${jobErr.message}`);
      if (!job) throw new Error(`recordVerdict: no job ${reference}`);

      const { error } = await db
        .from('damage_proposals')
        .update({ verdict, decided_at: new Date().toISOString() })
        .eq('job_id', (job as any).id)
        .eq('zone', zone)
        .eq('part_label', partLabel);
      if (error) throw new Error(`recordVerdict: ${error.message}`);
    },

    async recordOperation(reference, proposalKey, operation) {
      const [zone, partLabel] = proposalKey.split('::');
      const { data: job, error: jobErr } = await db
        .from('jobs').select('id').eq('reference', reference).maybeSingle();
      if (jobErr) throw new Error(`recordOperation: ${jobErr.message}`);
      if (!job) throw new Error(`recordOperation: no job ${reference}`);

      const { data: row, error: readErr } = await db
        .from('damage_proposals')
        .select('id, operation, proposed_operation')
        .eq('job_id', (job as any).id).eq('zone', zone).eq('part_label', partLabel)
        .maybeSingle();
      // Deploy order is not guaranteed: the label columns need peer auth on the
      // host, so a frontend can be live before the migration lands. The
      // estimate stays correct either way — the override is still in the draft
      // — so degrade to draft-only rather than throwing at somebody who cannot
      // do anything about it. Loud in the console, silent in the UI.
      if (isMissingLabelColumn(readErr)) {
        console.warn(
          '[estimator] repair/replace label NOT stored — migration '
          + '20260829000100_proposed_operation has not been applied. The estimate '
          + 'is unaffected; the training label for this override is being lost.');
        return;
      }
      if (readErr) throw new Error(`recordOperation: ${readErr.message}`);
      if (!row) throw new Error(`recordOperation: no proposal ${proposalKey}`);

      const r = row as any;
      // The rule lives in operationLabel.ts as a pure function, and is tested
      // there directly — it is the one piece of this that corrupts data
      // silently when it is wrong.
      const { data: auth } = await db.auth.getUser();
      const patch = operationPatch(
        r, operation, new Date().toISOString(), auth?.user?.id);
      if (!patch) return;

      const { error } = await db
        .from('damage_proposals').update(patch).eq('id', r.id);
      if (error) throw new Error(`recordOperation: ${error.message}`);
    },

    async issue(reference, grandTotal, lines, totals, currencyCode) {
      // The freeze itself is a trigger in the RLS migration — this only asks for
      // it. A client that could write an issued version would make the immutable
      // version a promise rather than a guarantee.
      const { data, error } = await db.schema('public').rpc('issue_estimate', {
        p_reference: reference,
        p_grand_total: grandTotal,
        p_lines: lines,
        p_totals: totals,
        p_currency_code: currencyCode,
      });
      if (error) throw new Error(`issue: ${error.message}`);
      const row = (Array.isArray(data) ? data[0] : data) as any;
      return { version: row?.version_no ?? 1, hash: row?.document_hash ?? '' };
    },

    async saveDraft(reference, draft) {
      const { error } = await db
        .from('jobs')
        .update({ draft, draft_saved_at: new Date().toISOString() })
        .eq('reference', reference);
      if (error) throw new Error(`saveDraft: ${error.message}`);
    },

    async removePhoto(reference, slot) {
      const { data: job, error: jobErr } = await db
        .from('jobs').select('id').eq('reference', reference).maybeSingle();
      if (jobErr) throw new Error(`removePhoto: ${jobErr.message}`);
      if (!job) throw new Error(`removePhoto: no job ${reference}`);

      const { data: rows, error: findErr } = await db
        .from('photos').select('id,storage_path')
        .eq('job_id', (job as any).id).eq('slot', slot);
      if (findErr) throw new Error(`removePhoto: ${findErr.message}`);
      if (!rows || rows.length === 0) return;

      // Files first. A row without its file shows a broken thumbnail; a file
      // without its row is merely unreferenced, and the retention sweep will
      // find it. Fail toward the harmless one.
      const paths = (rows as any[]).map((r) => r.storage_path).filter(Boolean);
      if (paths.length > 0) await db.storage.from('photos').remove(paths);

      const photoIds = (rows as any[]).map((r) => r.id);

      // Which proposals cited these photographs. Read BEFORE the delete, since
      // the evidence links cascade away with the photo rows.
      const { data: cited } = await db
        .from('proposal_evidence').select('proposal_id').in('photo_id', photoIds);
      const citedIds = [...new Set((cited ?? []).map((c: any) => c.proposal_id))];

      const { error: delErr } = await db
        .from('photos').delete().in('id', photoIds);
      if (delErr) throw new Error(`removePhoto: ${delErr.message}`);

      // Now drop any proposal whose evidence has ENTIRELY gone.
      //
      // Not every cited proposal: one that also cited a surviving photograph is
      // still evidenced and still true. Only the ones left standing on nothing,
      // because a proposal with no evidence is exactly what the gate calls an
      // invention — and leaving it would put a price on the estimate whose
      // justification the estimator has just thrown away.
      if (citedIds.length > 0) {
        const { data: remaining } = await db
          .from('proposal_evidence').select('proposal_id').in('proposal_id', citedIds);
        const stillEvidenced = new Set((remaining ?? []).map((r: any) => r.proposal_id));
        const orphaned = citedIds.filter((id) => !stillEvidenced.has(id));
        if (orphaned.length > 0) {
          const { error: propErr } = await db
            .from('damage_proposals').delete().in('id', orphaned);
          if (propErr) throw new Error(`removePhoto: ${propErr.message}`);
        }
      }

      // `unassessed` is deliberately left alone. Those entries say what the
      // model could not see, which is not a claim about any one photograph —
      // deleting them here would throw away the list of things that turn up at
      // teardown, on the strength of a guess. The next read refreshes them.
    },

    async askEstimate(reference, question) {
      const { data, error } = await db.functions.invoke('ask-estimate', {
        body: { reference, question },
      });
      // Same lesson as the photo read: supabase-js throws the body away, and
      // the server's actual message is the useful part.
      if (error) throw new Error(await explain(error));
      return {
        answer: String((data as any)?.answer ?? ''),
        refused: Boolean((data as any)?.refused),
      };
    },

    async analysePhotos(reference, provider) {
      // The function needs the job id, the shot slots actually taken, and which
      // zones those shots cover — the last one is what stops the model
      // proposing damage in an area nobody photographed. Assembled here rather
      // than server-side because the shot list is app logic and already tested.
      const job = await this.getJob(reference);
      if (!job) throw new Error(`analysePhotos: no job ${reference}`);

      // Unread photographs only. The server enforces this too — it is the
      // authority — but sending the short list keeps the request honest about
      // what it is asking for, and lets the button say "read 1 new photo".
      const unread = job.shots.filter((s) => s.status === 'taken' && !s.analysed);
      if (unread.length === 0) return null;
      const shotIds = unread.map((s) => s.id);

      // Zones are computed from EVERY usable photograph, not just the unread
      // ones. The gate asks "was this area ever photographed", and a panel
      // photographed on the first run is still photographed on the second.
      const takenIds = job.shots.filter((s) => s.status === 'taken').map((s) => s.id);
      const list = shotListFor(job.impact);
      const coveredZones = [...new Set(
        list.filter((spec) => takenIds.includes(spec.id)).flatMap((spec) => spec.covers))];

      const [year, ...rest] = job.vehicle.split(' ');
      const { data, error } = await db.functions.invoke('analyse-photos', {
        body: {
          jobId: job.id,
          shotIds,
          coveredZones,
          impactPoint: job.impact,
          vehicle: {
            year: /^\d{4}$/.test(year ?? '') ? Number(year) : undefined,
            make: rest[0],
            model: rest[1],
          },
          // Absent when the estimator left the picker on "Auto" — the server
          // falls back to its own default exactly as it did before this
          // existed.
          ...(provider ? { provider } : {}),
        },
      });
      if (error) {
        // supabase-js wraps any non-2xx in a FunctionsHttpError whose message
        // is the useless "Edge Function returned a non-2xx status code" — the
        // actual body, which is where the function explains itself, is only
        // reachable through `context`. Without this the function's careful
        // messages ("GEMINI_API_KEY is not set", a quota notice, "job not
        // found") all arrive as the same shrug, and the app looks like it did
        // nothing at all.
        throw new Error(await explain(error));
      }

      const proposals = (data as any)?.proposals;
      return {
        added: Array.isArray(proposals) ? proposals.length : 0,
        provider: String((data as any)?.provider ?? ''),
        model: String((data as any)?.model ?? ''),
      };
    },
  };
}
